import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  TrophyOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PATHS } from '../../routes/paths';
import {
  useCompleteInterviewMutation,
  useGetAIInterviewQuestionsQuery,
  useGetAppliedJobsQuery,
  useGetAssessmentInterviewQuery,
  useGetPreassignedS3UrlToUploadAudioQuery,
} from '../../app/features/recruitment/recruitment.api';
import EmptyState from '../../components/EmptyState';
import LoginModal from '../../components/LoginModal';
import useAuth from '../../hooks/useAuth';
import useDiscloser from '../../hooks/useDiscloser';
import {
  CameraMicrophoneIcon,
  RecordIcon,
  VideoCameraAiIcon,
} from '../../icons';
import WebLayout from '../../layouts/WebLayout';
import { toast } from '../../lib/toast';

/**
 * TakeInterview — Professional Interview Dashboard
 *
 * ▸ Shows one question at a time with dynamic progress dashes.
 * ▸ 30-second countdown per question; auto-stops when timer expires.
 * ▸ On "Next": PUTs audio to the index-matched presigned S3 URL.
 * ▸ On "Finish" (last question): shows confirm modal, uploads last
 *   audio, then calls completeInterview with all file_names.
 * ▸ Current question index is persisted in localStorage.
 */

type RecordingStatus = 'idle' | 'recording' | 'stopped';

const LS_KEY = (jobAppId: string) => `interview_q_idx_${jobAppId}`;
const RECORDING_LIMIT = 30;

const TakeInterview = () => {
  const { access_token, verifiedInfo } = useAuth();
  const { applicationId: jobApplicationId } = useParams();
  const navigate = useNavigate();
  const [loginConfig, handleOpenLogin, handleCloseLogin] = useDiscloser();

  // ── Refs ──────────────────────────────────────────────────────────────
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wasAutoStoppedRef = useRef(false);
  const [isStarted, setIsStarted] = useState(false);
  const [shouldAutoNext, setShouldAutoNext] = useState(false);

  // ── State ─────────────────────────────────────────────────────────────
  const [status, setStatus] = useState<RecordingStatus>('idle');
  const [mediaErrors, setMediaErrors] = useState({
    video: false,
    audio: false,
  });
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMediaErrorModal, setShowMediaErrorModal] = useState(false);
  const [isMediaLoading, setIsMediaLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(
    dayjs().format('YYYY/MM/DD | h:mm A'),
  );
  const [recordingTimeLeft, setRecordingTimeLeft] = useState(RECORDING_LIMIT);

  // ── Question index (persisted) ────────────────────────────────────────
  const [currentIndex, setCurrentIndex] = useState<number>(() => {
    if (!jobApplicationId) return 0;
    const stored = localStorage.getItem(LS_KEY(jobApplicationId));
    return stored ? parseInt(stored, 10) : 0;
  });

  // ── API queries & mutations ───────────────────────────────────────────
  const applicantId = verifiedInfo?.id;

  const { data: interviewData } = useGetAssessmentInterviewQuery(
    {
      applicant_id: applicantId as string,
      job_application_id: jobApplicationId as string,
    },
    { skip: !applicantId || !jobApplicationId },
  );
  const aiInterviewId = interviewData?.data?.interview?.ai_interview_id;

  const { data: interviewQuestionsData } = useGetAIInterviewQuestionsQuery(
    {
      ai_interview_id: aiInterviewId as string,
      applicant_id: applicantId as string,
    },
    { skip: !aiInterviewId || !applicantId },
  );

  const { data: preAssignedUrlsData } =
    useGetPreassignedS3UrlToUploadAudioQuery(
      {
        ai_interview_id: aiInterviewId as string,
        applicant_id: applicantId as string,
      },
      { skip: !aiInterviewId || !applicantId },
    );
  const { data: appliedJobsData } = useGetAppliedJobsQuery(
    { applicant_id: applicantId as string },
    { skip: !applicantId },
  );

  const [completeInterview] = useCompleteInterviewMutation();

  // ── Derived values ────────────────────────────────────────────────────
  const questions = useMemo(
    () => interviewQuestionsData?.data?.questions ?? [],
    [interviewQuestionsData],
  );
  const presignedUrls = useMemo(
    () => preAssignedUrlsData?.data?.urls ?? [],
    [preAssignedUrlsData],
  );
  const jobName = useMemo(() => {
    if (!appliedJobsData?.data?.data) return '';
    const job = appliedJobsData.data.data.find(
      (j) => j.job_application_id === jobApplicationId,
    );
    return job?.job_title ?? '';
  }, [appliedJobsData, jobApplicationId]);
  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex] ?? null;
  const isLastQuestion = currentIndex >= totalQuestions - 1;

  const { mediaErrorTitle, mediaErrorMessage, mediaErrorIcon } = useMemo(() => {
    if (mediaErrors.video && mediaErrors.audio) {
      return {
        mediaErrorTitle: 'Camera & Microphone Access Required',
        mediaErrorMessage:
          'Both camera and microphone access are required to proceed with the interview. Please allow access in your browser settings.',
        mediaErrorIcon: <CameraMicrophoneIcon size={48} />,
      };
    }
    if (mediaErrors.video) {
      return {
        mediaErrorTitle: 'Camera Access Required',
        mediaErrorMessage:
          'Camera access is required for the interview. Please allow camera permissions to proceed.',
        mediaErrorIcon: <VideoCameraAiIcon size={48} />,
      };
    }
    if (mediaErrors.audio) {
      return {
        mediaErrorTitle: 'Microphone Access Required',
        mediaErrorMessage:
          'Microphone access is required for the interview. Please allow microphone permissions to proceed.',
        mediaErrorIcon: <RecordIcon size={48} />,
      };
    }
    return {
      mediaErrorTitle: '',
      mediaErrorMessage: null,
      mediaErrorIcon: null,
    };
  }, [mediaErrors]);

  const isAlreadySubmitted =
    !!interviewData?.data?.interview?.is_submitted_ai_interview;

  // ── Clock ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs().format('YYYY/MM/DD | h:mm A'));
    }, 60_000);
    return () => clearInterval(timer);
  }, []);

  // ── Media init ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setIsMediaLoading(true);
      let videoOk = false;
      let audioOk = false;

      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        if (cancelled) {
          videoStream.getTracks().forEach((t) => t.stop());
          return;
        }
        videoStreamRef.current = videoStream;
        if (videoRef.current) videoRef.current.srcObject = videoStream;
        videoOk = true;
      } catch (err) {
        console.error('Failed to access camera:', err);
      }

      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: true,
        });
        if (cancelled) {
          audioStream.getTracks().forEach((t) => t.stop());
          return;
        }
        audioStreamRef.current = audioStream;
        audioOk = true;
      } catch (err) {
        console.error('Failed to access microphone:', err);
      }

      if (!cancelled) {
        setMediaErrors({ video: !videoOk, audio: !audioOk });
        setIsMediaLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
      videoStreamRef.current?.getTracks().forEach((t) => t.stop());
      audioStreamRef.current?.getTracks().forEach((t) => t.stop());
      mediaRecorderRef.current = null;
    };
  }, []);

  // ── Sync video stream when starting ──────────────────────────────────
  useEffect(() => {
    if (isStarted && videoRef.current && videoStreamRef.current) {
      videoRef.current.srcObject = videoStreamRef.current;
    }
  }, [isStarted]);

  // ── Persist current index ─────────────────────────────────────────────
  useEffect(() => {
    if (jobApplicationId) {
      localStorage.setItem(LS_KEY(jobApplicationId), String(currentIndex));
    }
  }, [currentIndex, jobApplicationId]);

  // ── Recording handlers ────────────────────────────────────────────────
  const stopCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const handleStopRecording = useCallback(
    (auto = false) => {
      stopCountdown();
      setRecordingTimeLeft(RECORDING_LIMIT);
      wasAutoStoppedRef.current = auto;
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop();
        setStatus('stopped');
        if (auto) {
          toast('info', '⏱️ Time is up! Recording stopped automatically.');
        }
      }
    },
    [stopCountdown],
  );

  const handleStartRecording = useCallback(() => {
    if (!audioStreamRef.current) {
      setMediaErrors((prev) => ({ ...prev, audio: true }));
      return;
    }

    audioChunksRef.current = [];
    const recorder = new MediaRecorder(audioStreamRef.current, {
      mimeType: 'audio/webm',
    });

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) audioChunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      setAudioBlob(blob);
    };

    recorder.start();
    mediaRecorderRef.current = recorder;
    setStatus('recording');
    setMediaErrors((prev) => ({ ...prev, audio: false }));
    setAudioBlob(null);
    wasAutoStoppedRef.current = false;

    // Start countdown
    setRecordingTimeLeft(RECORDING_LIMIT);
    stopCountdown();
    let remaining = RECORDING_LIMIT;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setRecordingTimeLeft(remaining);
      if (remaining <= 0) {
        handleStopRecording(true);
      }
    }, 1000);
  }, [stopCountdown, handleStopRecording]);

  // ── Auto-start recording ─────────────────────────────────────────────
  useEffect(() => {
    if (isStarted && currentIndex < totalQuestions && status === 'idle') {
      handleStartRecording();
    }
  }, [isStarted, currentIndex, totalQuestions, status, handleStartRecording]);

  // ── Upload helper ─────────────────────────────────────────────────────
  const uploadCurrentAudio = useCallback(async (): Promise<boolean> => {
    if (!audioBlob) {
      toast('warning', 'Please record your answer first.');
      return false;
    }
    const targetUrl = presignedUrls[currentIndex]?.url;
    if (!targetUrl) {
      toast('error', 'Pre-signed upload URL not found for this question.');
      return false;
    }

    const response = await fetch(targetUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'audio/webm' },
      body: audioBlob,
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }
    return true;
  }, [audioBlob, presignedUrls, currentIndex]);

  // ── Next button handler ───────────────────────────────────────────────
  const handleNext = useCallback(async () => {
    if (status === 'recording') {
      setShouldAutoNext(true);
      handleStopRecording();
      return;
    }

    if (!audioBlob) {
      toast('warning', 'Please wait for the recording to finish.');
      return;
    }

    // Last question → show confirm modal instead of uploading immediately
    if (isLastQuestion) {
      setShowConfirmModal(true);
      return;
    }

    setIsUploading(true);
    try {
      await uploadCurrentAudio();
      toast('success', 'Answer uploaded successfully!');
      setCurrentIndex((prev) => prev + 1);
      setAudioBlob(null);
      setStatus('idle');
    } catch (err) {
      console.error('Upload Error:', err);
      toast('error', 'An unexpected error occurred during upload.');
    } finally {
      setIsUploading(false);
    }
  }, [
    status,
    audioBlob,
    isLastQuestion,
    uploadCurrentAudio,
    handleStopRecording,
  ]);

  // ── Handle pending next (after onstop sets audioBlob) ────────────────
  useEffect(() => {
    if (audioBlob && shouldAutoNext && status === 'stopped') {
      setShouldAutoNext(false);
      handleNext();
    }
  }, [audioBlob, shouldAutoNext, status, handleNext]);

  // ── Confirm finish handler ────────────────────────────────────────────
  const handleConfirmFinish = useCallback(async () => {
    setShowConfirmModal(false);
    setIsUploading(true);
    try {
      // 1. Upload last question audio
      await uploadCurrentAudio();
      // toast('success', 'Last answer uploaded!');

      // 2. Call completeInterview — body is the full array of { s3_url, file_name }
      //    mapping .url (GET type field) → s3_url (what the POST endpoint expects)
      const body = presignedUrls.map((u) => ({
        s3_url: u.url,
        file_name: u.file_name,
      }));
      await completeInterview({
        applicant_id: applicantId as string,
        ai_interview_id: aiInterviewId as string,
        job_application_id: jobApplicationId as string,
        data: body,
      });

      // 3. Clean up and navigate
      if (jobApplicationId) localStorage.removeItem(LS_KEY(jobApplicationId));
      toast('success', 'Interview completed!');
      navigate(PATHS.jobPortal.applications);
    } catch (err) {
      console.error('Finish Error:', err);
      toast('error', 'Something went wrong while finishing. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [
    uploadCurrentAudio,
    presignedUrls,
    completeInterview,
    applicantId,
    aiInterviewId,
    jobApplicationId,
    navigate,
  ]);

  return (
    <WebLayout>
      <section className="min-h-[calc(100vh-80px)] bg-[#F8FAFC] py-10 px-4 flex items-center justify-center">
        {access_token ? (
          <div className="w-full max-w-5xl bg-[#1a2333] rounded-[24px] shadow-lg p-6 md:p-10 text-white">
            {/* Top Bar */}
            <div className="flex flex-wrap justify-between items-center mb-8 gap-4 border-b border-white/5 pb-4">
              <div className="flex items-center gap-2">
                <span className="text-white/60 text-lg font-medium tracking-tight">
                  Meeting :
                </span>
                <span className="text-xl font-bold bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent italic">
                  {jobName ? `Interview for ${jobName}` : 'AI Interview'}
                </span>
              </div>
              <div className="text-white/60 text-lg font-mono">
                {currentTime}
              </div>
            </div>

            {/* Main Content Area */}
            <div className="grid grid-cols-1 gap-8">
              {isAlreadySubmitted ? (
                <EmptyState
                  icon={
                    <CheckCircleOutlined className="text-4xl text-teal-400" />
                  }
                  title="Interview Already Submitted"
                  description="You have already completed this interview. You cannot retake it."
                  className="py-12"
                />
              ) : !isStarted && currentIndex === 0 ? (
                <div className="max-w-3xl mx-auto w-full space-y-10 py-10 animate-fade-in">
                  <div className="text-center space-y-4">
                    <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                      Ready to Start Your Interview?
                    </h2>
                    <p className="text-white/60 text-lg max-w-xl mx-auto">
                      Please review the instructions below to ensure a smooth
                      experience. Good luck!
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-8 rounded-[32px] bg-white/5 border border-white/10 space-y-5 hover:bg-white/[0.07] transition-colors group">
                      <div className="flex items-center gap-3 text-teal-400">
                        <div className="size-10 rounded-xl bg-teal-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <InfoCircleOutlined className="text-xl" />
                        </div>
                        <h3 className="font-bold text-xl">Role Details</h3>
                      </div>
                      <div className="space-y-4">
                        <div className="space-y-1">
                          <p className="text-white/40 text-xs uppercase tracking-widest font-semibold">
                            Position
                          </p>
                          <p className="text-white text-lg font-medium">
                            {jobName || 'AI Interview Candidate'}
                          </p>
                        </div>
                        <div className="space-y-2 bg-amber-500/5 p-4 rounded-2xl border border-amber-500/10">
                          <p className="text-amber-400 text-xs uppercase tracking-widest font-bold flex items-center gap-2">
                            <WarningOutlined /> Integrity Note
                          </p>
                          <p className="text-white/70 text-xs leading-relaxed">
                            The use of AI assistance or external resources is
                            strictly prohibited. Detection of unauthorized aids
                            will result in{' '}
                            <span className="text-amber-400 font-semibold">
                              automatic disqualification
                            </span>{' '}
                            from the recruitment process.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-8 rounded-[32px] bg-white/5 border border-white/10 space-y-5 hover:bg-white/[0.07] transition-colors group">
                      <div className="flex items-center gap-3 text-teal-400">
                        <div className="size-10 rounded-xl bg-teal-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <FileTextOutlined className="text-xl" />
                        </div>
                        <h3 className="font-bold text-xl">Quick Rules</h3>
                      </div>
                      <ul className="space-y-3">
                        {[
                          'Recording starts automatically',
                          '30-second time limit per answer',
                          'Click "Next" to stop and save your answer early',
                          'Camera & Microphone must stay on at all times',
                        ].map((rule, idx) => (
                          <li
                            key={idx}
                            className="flex items-center gap-3 text-white/70 text-sm"
                          >
                            <div className="size-1.5 rounded-full bg-teal-500 shadow-[0_0_8px_rgba(20,184,166,0.5)]" />
                            {rule}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-6 pt-6">
                    <Button
                      size="large"
                      type="primary"
                      className="btn-primary min-w-[280px] h-16 !rounded-2xl text-xl font-bold shadow-[0_10px_25px_rgba(20,184,166,0.2)] hover:shadow-[0_15px_35px_rgba(20,184,166,0.3)] transition-all"
                      onClick={() => {
                        if (mediaErrorMessage) {
                          setShowMediaErrorModal(true);
                        } else {
                          setIsStarted(true);
                        }
                      }}
                      loading={isMediaLoading}
                      icon={<PlayCircleOutlined />}
                    >
                      Start Interview
                    </Button>
                    <div className="flex items-center gap-2 text-white/40 text-sm bg-white/5 px-4 py-2 rounded-full border border-white/10">
                      <ClockCircleOutlined />
                      <span>The timer will start with the first question</span>
                    </div>
                  </div>
                </div>
              ) : mediaErrorMessage ? (
                <EmptyState
                  icon={mediaErrorIcon}
                  title={mediaErrorTitle}
                  description={mediaErrorMessage}
                  className="py-12"
                />
              ) : (
                <>
                  {/* Video Preview Card */}
                  <div className="relative group rounded-3xl overflow-hidden bg-black/40 ring-1 ring-white/10 aspect-[21/9] max-h-[480px]">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover opacity-90 transition-opacity duration-700"
                      style={{ transform: 'scaleX(-1)' }}
                    />

                    {/* Overlays */}
                    <div className="absolute inset-0 p-6 flex flex-col justify-between pointer-events-none">
                      {/* Overlay Top */}
                      <div className="flex justify-between items-start w-full">
                        <div className="bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-xl text-sm font-medium">
                          {verifiedInfo?.name ?? 'Candidate'}
                        </div>
                        {totalQuestions > 0 && (
                          <div className="bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-xl text-sm font-mono tabular-nums">
                            Question {currentIndex + 1} / {totalQuestions}
                          </div>
                        )}
                      </div>

                      {/* Recording Indicator */}
                      {status === 'recording' && (
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
                          <div className="size-4 bg-red-500 rounded-full animate-pulse shadow-[0_0_15px_rgba(239,68,68,0.8)]" />
                          <span className="text-red-500 font-bold text-xs uppercase tracking-widest bg-black/40 px-2 py-1 rounded backdrop-blur-sm">
                            Recording Live
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* AI Segment */}
                  <div className="pt-4 px-4 space-y-8">
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-semibold opacity-90 tracking-wide">
                        Ai Assisting
                      </span>
                      <span className="text-teal-400 text-xl">✨</span>
                    </div>

                    {/* Dynamic Progress Dashes */}
                    {totalQuestions > 0 && (
                      <div className="flex gap-2 w-full">
                        {Array.from({ length: totalQuestions }).map((_, i) => (
                          <div
                            key={i}
                            className={`h-[6px] flex-1 rounded-full transition-all duration-500 ${
                              i <= currentIndex ? 'bg-teal-500' : 'bg-white/10'
                            }`}
                          />
                        ))}
                      </div>
                    )}

                    {/* Question Text */}
                    {currentQuestion ? (
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-b border-white/5 pb-10">
                        <h2 className="text-xl md:text-2xl font-semibold leading-relaxed max-w-[85%] text-white/90 transition-all duration-300">
                          {currentQuestion.question}
                        </h2>
                        <div
                          className={`flex items-center gap-3 px-4 py-2 rounded-2xl shrink-0 transition-colors duration-300 ${
                            status === 'recording'
                              ? recordingTimeLeft <= 5
                                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                : recordingTimeLeft <= 10
                                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                                  : 'bg-white/5 text-white/80'
                              : 'bg-white/5 text-white/80'
                          }`}
                        >
                          <ClockCircleOutlined
                            className={`transition-colors duration-300 ${
                              status === 'recording'
                                ? recordingTimeLeft <= 5
                                  ? 'text-red-400'
                                  : recordingTimeLeft <= 10
                                    ? 'text-orange-400'
                                    : 'text-teal-400'
                                : 'text-teal-400'
                            }`}
                          />
                          <span className="font-mono text-lg italic">
                            {status === 'recording'
                              ? `${recordingTimeLeft} sec`
                              : '30 sec'}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="border-b border-white/5 pb-10">
                        <p className="text-white/40 text-lg">
                          Loading questions…
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-between items-center">
                      <div className="h-10">
                        {status === 'stopped' && (
                          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-400 animate-fade-in text-sm font-medium">
                            <CheckCircleOutlined />
                            Answer recorded — ready to upload
                          </div>
                        )}
                      </div>
                      <Button
                        size="large"
                        className="btn-primary min-w-[140px]"
                        onClick={handleNext}
                        loading={isUploading}
                        disabled={isUploading}
                      >
                        {isLastQuestion ? 'Finish' : 'Next'}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="w-full max-w-5xl bg-[#1a2333] rounded-[24px] shadow-lg p-6 md:p-10 text-white">
            <EmptyState
              icon={<VideoCameraAiIcon size={40} />}
              title="Authentication Required"
              description="To proceed with your scheduled AI interview, please log in to verify your identity and sync your preparation records."
              className="py-12"
            />

            <div className="flex justify-center pb-10">
              <Button
                size="large"
                type="primary"
                className="btn-primary"
                onClick={() => handleOpenLogin({})}
              >
                Log In to Start Interview
              </Button>
            </div>
          </div>
        )}
      </section>

      <LoginModal
        open={loginConfig.open}
        handleClose={handleCloseLogin}
        title="Secure Interview Login"
      />

      {/* ── Media Error Modal ────────────────────────────────────────── */}
      {showMediaErrorModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={() => setShowMediaErrorModal(false)}
          />
          <div className="relative w-full max-w-md bg-[#1a2333] border border-white/10 rounded-[40px] shadow-2xl p-10 flex flex-col items-center gap-6 text-white animate-fade-in">
            <div className="size-20 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400">
              {mediaErrorIcon}
            </div>
            <div className="text-center space-y-3">
              <h2 className="text-2xl font-bold tracking-tight">
                {mediaErrorTitle}
              </h2>
              <p className="text-white/55 text-sm leading-relaxed">
                {mediaErrorMessage}
              </p>
            </div>
            <Button
              size="large"
              block
              className="!rounded-2xl !h-auto !py-4 btn-primary font-semibold text-lg"
              onClick={() => setShowMediaErrorModal(false)}
            >
              Got it, let me check
            </Button>
          </div>
        </div>
      )}

      {/* ── Finish Confirm Modal ─────────────────────────────────────── */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => !isUploading && setShowConfirmModal(false)}
          />

          {/* Card */}
          <div className="relative w-full max-w-md bg-[#1a2333] border border-white/10 rounded-3xl shadow-2xl p-8 flex flex-col gap-6 text-white animate-fade-in">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="size-20 rounded-full bg-gradient-to-br from-teal-500/20 to-teal-400/5 border border-teal-500/30 flex items-center justify-center">
                <TrophyOutlined className="text-4xl text-teal-400" />
              </div>
            </div>

            {/* Heading */}
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold tracking-tight">
                Submit Interview?
              </h2>
              <p className="text-white/55 text-sm leading-relaxed">
                You've reached the last question. Once you confirm, your
                responses will be submitted for review and{' '}
                <span className="text-white/80 font-medium">
                  cannot be changed.
                </span>
              </p>
            </div>

            {/* Stats row */}
            <div className="flex justify-center gap-6 py-3 px-4 rounded-2xl bg-white/5 border border-white/8">
              <div className="text-center">
                <p className="text-2xl font-bold text-teal-400">
                  {totalQuestions}
                </p>
                <p className="text-white/50 text-xs mt-0.5">Questions</p>
              </div>
              <div className="w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl font-bold text-teal-400">
                  {totalQuestions}
                </p>
                <p className="text-white/50 text-xs mt-0.5">Answered</p>
              </div>
            </div>

            {/* Warning note */}
            <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <WarningOutlined className="text-amber-400 mt-0.5 shrink-0" />
              <p className="text-amber-300/80 text-xs leading-relaxed">
                Make sure you are satisfied with all your answers before
                submitting. This action is irreversible.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-1">
              <button
                disabled={isUploading}
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-3 rounded-2xl border border-white/15 text-white/70 hover:bg-white/5 hover:text-white transition-all text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Go Back
              </button>
              <Button
                size="large"
                className="flex-1 !rounded-2xl !h-auto !py-3 btn-primary"
                onClick={handleConfirmFinish}
                loading={isUploading}
                disabled={isUploading}
              >
                {isUploading ? 'Submitting…' : 'Submit Interview'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </WebLayout>
  );
};

export default TakeInterview;
